import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminDashboard from "../../components/AdminDashboard";
import OrderDisplay from "../../components/OrderDisplay";
import { assignOrder, cancelTakenOrder, completeOrder, deleteOrder, getOrder } from "../../service/OrderService";
import { Driver, Order } from "../../my-type";
import { useParams } from "react-router-dom";
import PageBase from "../../components/PageBase";
import Header from "../../layouts/Header";
import { OrderActionType, OrderStatus, UserRole } from "../../my-enum";
import { Button, Col, Row } from "rsuite";
import DriverSearchModal from "../../components/DriverSearchModal";

const OrderDetail: React.FC = () => {
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [driverSearchOpen, setDriverSearchOpen] = useState(false);
  const [messageModal, setMessageModal] = useState({
    open: false,
    type: 'info' as 'info' | 'error',
    title: '',
    message: '',
    onCancel: null as (() => void) | null,
    onConfirm: null as (() => void) | null
  });


  useEffect(() => {
    const fetchOrder = async () => {
      if (orderId) {
        try {
          const response = await getOrder(orderId);
          if (response) {
            setOrder(response);
          }
        } catch (error) {
          console.error('Failed to fetch order:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchOrder();
  }, [orderId]);

  const showInfoModal = (message: string, title: string = '訊息', onCancel?: () => void, onConfirm?: () => void) => {
    setMessageModal({
      open: true,
      type: 'info',
      title,
      message,
      onCancel: onCancel || null,
      onConfirm: onConfirm || null
    });
  };

  const showConfirmModal = (message: string, title: string = '確認', onCancel: () => void, onConfirm: () => void) => {
    setMessageModal({
      open: true,
      type: 'info',
      title,
      message,
      onCancel,
      onConfirm
    });
  }

  const showErrorModal = (message: string, title: string = '錯誤', onCancel?: () => void, onConfirm?: () => void) => {
    setMessageModal({
      open: true,
      type: 'error',
      title,
      message,
      onCancel: onCancel || null,
      onConfirm: onConfirm || null
    });
  };

  const closeMessageModal = () => {
    setMessageModal({
      ...messageModal,
      open: false
    });
  };

  const handleModalConfirm = () => {
    if (messageModal.onConfirm) {
      messageModal.onConfirm();
    }
    closeMessageModal();
  };

  if (loading) {
    return <div>Loading...</div>;
  }
  if (!order) {
    return <div>No order found.</div>;
  }

  const onAdminClick = async (order: Order, actionType: OrderActionType) => {
    if (actionType === OrderActionType.Abort) {
      let takeResult = await cancelTakenOrder(order.orderID);

      console.log(takeResult);

      if (takeResult) {
        if (takeResult.statusCode === 200) {
          showInfoModal("已取消司機接單", '訊息', undefined, () => {
            setOrder({ ...order, status: OrderStatus.派車中, driver: null, driverUID: 0 });
            //navigate(-1);
          });
          return;
        } else {
          showErrorModal(takeResult.message);

        }
      }
    }

    if (actionType === OrderActionType.DeleteOrder) {

      // After deleting, navigate back to order list
      showConfirmModal(`要刪除訂單${order.orderID}嗎？`, '確認', () => { closeMessageModal(); }, async () => {
        const deleteResult = await deleteOrder(order.orderID);
        console.log(deleteResult);
        if (deleteResult.success) {
          navigate('/orders');
        } else {
          showErrorModal(`刪除訂單失敗: ${deleteResult.error}`);
        }
      });
    }

    if (actionType === OrderActionType.CompleteOrder) {

      //check if order status is OrderStatus.載客中
      if (order.status !== OrderStatus.載客中) {
        showErrorModal(`只有載客中狀態的訂單才能標記為完成`);
        return;
      }
      //show confirm modal
      showConfirmModal(`確定要將訂單${order.orderID}標記為已完成嗎？`, '確認', () => { closeMessageModal(); }, async () => {
        //update order status to completed
        const updateResult = await completeOrder(order.orderID);
        if (updateResult?.statusCode !== 200) {
          showErrorModal(`更新訂單狀態失敗`);
          return;
        }
        setOrder({ ...order, status: OrderStatus.已完成 });
        closeMessageModal();
      });
    }

    if (actionType === OrderActionType.ReAssign) {
      setDriverSearchOpen(true);
    }
  }

  const header = <Header HeaderTitle="訂單明細" isShowRefresh={false} OnReturn={() => navigate(-1)} OnRefresh={() => { }} />;


  return (
    <AdminDashboard title="訂單詳細資訊">
      <PageBase header={header}
        content={
          <>
            <OrderDisplay
              order={order}
              isShowDetail={true}
              userRole={UserRole.Lead}
              onOrderAction={onAdminClick}
              onOrderUpdated={(updatedOrder) => setOrder(structuredClone(updatedOrder))}
            />
            <DriverSearchModal
              open={driverSearchOpen}
              onClose={() => setDriverSearchOpen(false)}
              onSelect={async (driver: Driver) => {
                if (!order) {
                  return;
                }
                const result = await assignOrder(order.orderID, driver.userUID);
                if (!result || result.statusCode !== 200) {
                  showErrorModal(result?.message || "指派司機失敗");
                  return;
                }
                const updatedOrder = await getOrder(order.orderID);
                if (updatedOrder) {
                  setOrder(structuredClone(updatedOrder));
                }
                showInfoModal(`已指派司機 ${driver.driverName}`);
              }}
              teamCode={order.teamCode}
              title="指派司機"
            />
            {messageModal.open && (
              <div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  width: '100vw',
                  height: '100vh',
                  background: 'rgba(0,0,0,0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 9999
                }}
              >
                <div style={{ background: '#fff', padding: 24, borderRadius: 8, minWidth: 300 }}>
                  <h4>{messageModal.title}</h4>
                  <p>{messageModal.message}</p>
                  <Row gutter={16} style={{ marginTop: 16 }}>

                    {messageModal.onCancel && (
                      <Col>
                        <Button appearance="subtle" onClick={() => {
                          if (messageModal.onCancel) {
                            messageModal.onCancel();
                          }
                          closeMessageModal();
                        }}>
                          取消
                        </Button>
                      </Col>
                    )}

                    <Col>
                      <Button appearance="primary" onClick={handleModalConfirm}>
                        確認
                      </Button>
                    </Col>
                    {/* <Col>
                      <Button appearance="subtle" onClick={closeMessageModal}>
                        關閉
                      </Button>
                    </Col> */}
                  </Row>
                </div>
              </div>
            )}
          </>
        }
        showSide={false} />
    </AdminDashboard>
  );
}

export default OrderDetail;
